import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import ReactQuill from 'react-quill';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

import icon from '../../assets/support-icon4.svg';
import uploadBtn from '../../assets/fileUpload.svg';
import clip from '../../assets/clip.svg';
import fileIcon from '../../assets/fileIcon.svg';
import closeIcon from '../../assets/closeIcon.svg';

interface FAQProps {
  mode: 'create' | 'edit';
  onEditSuccess?: () => void;
}

interface FAQFormData {
  user: string;
  question: string;
  answer: string;
  files: File[];
}

interface ExistingFile {
  originalName: string;
  _id: string;
}

const FAQ: React.FC<FAQProps> = ({ onEditSuccess }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [mode, setMode] = useState<'create' | 'edit'>('create');

  const fileLimit = 5;
  const [newFiles, setNewFiles] = useState<File[]>([]); // 새로 선택한 파일
  const [existingFiles, setExistingFiles] = useState<ExistingFile[]>([]); // 서버에서 가져온 기존 파일
  const [deletedFileIds, setDeletedFileIds] = useState<string[]>([]); // 삭제 예정 기존 파일 ID
  const [fileCount, setFileCount] = useState<number>(0);

  const quillRef = useRef<ReactQuill | null>(null);

  const {
    handleSubmit,
    register,
    control,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<FAQFormData>();

  useEffect(() => {
    if (id) {
      setMode('edit');
    } else {
      setMode('create');
    }
  }, [id]);

  // ReactQuill 내용 검증
  const validateQuillContent = (value: string) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = value;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    return textContent.trim().length > 0 || '내용을 입력해주세요';
  };

  // 파일 삭제 핸들러
  const handleNewFileRemove = (index: number) => {
    const updated = [...newFiles];
    updated.splice(index, 1);
    setNewFiles(updated);
  };

  // 기존 파일 삭제 핸들러
  const handleExistingFileRemove = (id: string) => {
    setExistingFiles(prev => prev.filter(f => f._id !== id)); // UI에서 제거
    setDeletedFileIds(prev => [...prev, id]);
  };

  // 파일 입력 변경
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const combined = [...newFiles, ...files].slice(0, fileLimit);
    setNewFiles(combined);
  };

  const onSubmit = async (data: FAQFormData) => {
    let uploadedIds: string[] = [];

    // 1) 새 파일 업로드
    if (newFiles.length > 0) {
      const formData = new FormData();
      newFiles.forEach(file => formData.append('files', file));
      formData.append('usedFor', 'faq');

      const uploadRes = await axios.post(`/api/upload-files/${'faq'}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      uploadedIds = uploadRes.data.fileIds;
    }

    // 2) 모든 파일 ID 합치기 (기존 + 새 업로드)
    const allFileIds = [...existingFiles.map(f => f._id), ...uploadedIds];

    // 3) 삭제된 파일 DB 처리 (deleted:true)
    if (deletedFileIds.length > 0) {
      deletedFileIds.forEach(async id => {
        try {
          await axios.put(`/api/upload-files/mark-deleted/${id}`, { deleted: true });
        } catch (err) {
          console.error(`Error deleting file ${id}:`, err);
        }
      });
    }

    // 4) FAQ 등록/수점
    const payload = {
      user: user?.id || '',
      question: data.question,
      answer: data.answer,
      files: allFileIds,
    };

    if (mode === 'edit') {
      try {
        const response = await axios.put(`/api/faq/${id}`, payload);
        if (response.data) {
          alert('FAQ가 수정되었습니다.');
          navigate(`/setting/faq-list`);
          onEditSuccess?.();
        } else {
          alert('FAQ 수정에 실패했습니다. 다시 시도해주세요.');
        }
      } catch (error) {
        console.error('FAQ 수정 실패:', error);
        alert('FAQ 수정에 실패했습니다. 다시 시도해주세요.');
      }
    } else {
      try {
        await axios.post('/api/faq/create', payload);
        alert('FAQ가 등록되었습니다.');
        onEditSuccess?.();
        navigate('/setting/faq-list');
      } catch (err) {
        console.error(err);
      }
    }
  };

  useEffect(() => {
    setFileCount(existingFiles.length + newFiles.length);
  }, [existingFiles, newFiles]);

  useEffect(() => {
    if (fileCount > fileLimit) {
      alert('첨부파일은 최대 ' + fileLimit + '개까지 선택 가능합니다.');
    }
  }, [fileCount, fileLimit]);

  // id값이 있으면 faq detail api call
  useEffect(() => {
    if (id) {
      const fetchFAQDetail = async () => {
        try {
          const response = await axios.get(`/api/faq/${id}`);
          reset(response.data);
          setExistingFiles(response.data.fileList || []);
        } catch (error) {
          console.error('Error fetching FAQ detail:', error);
        }
      };
      fetchFAQDetail();
    }
  }, [id, reset]);

  return (
    <div className="faq-container">
      <div className="page-header">
        <img src={icon} alt="Mind Thermometer Icon" className="page-header__icon" />
        <h2 className="page-header__title">FAQ</h2>
        <p>자주 묻는 질문을 확인하세요.</p>
      </div>
      <form className="faq-container__form" onSubmit={handleSubmit(onSubmit)}>
        <div className="form-group">
          <label htmlFor="question">질문</label>
          <input
            type="text"
            id="question"
            placeholder="질문을 입력해주세요"
            {...register('question', { required: '질문을 입력해주세요' })}
          />
          {errors.question && <span className="error-message">{errors.question.message}</span>}
        </div>
        <div className="form-group">
          <label htmlFor="answer">답변</label>
          <div translate="no">
            <Controller
              name="answer"
              control={control}
              rules={{ validate: validateQuillContent }}
              render={({ field }) => (
                <ReactQuill
                  id="answer"
                  className="quill-input"
                  theme="snow"
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="답변을 입력해주세요"
                  ref={quillRef}
                />
              )}
            />
          </div>
          {errors.answer && <span className="error-message">{errors.answer.message}</span>}
        </div>

        <div className="form-group">
          <div className="file-label-group">
            <label htmlFor="files">첨부파일</label>
            <span className="count">
              {fileCount}/{fileLimit}
            </span>
          </div>

          {fileCount < fileLimit && (
            <>
              <input
                type="file"
                id="files"
                className="file-input"
                multiple
                onChange={handleFileChange}
              />
              <label htmlFor="files" className="file-upload-btn">
                <div>
                  <img src={uploadBtn} alt="파일업로드" />
                  <span> {fileCount > 0 ? '파일 추가' : '파일 선택'}</span>
                </div>
                <img src={clip} alt="클립" />
              </label>
            </>
          )}

          <div className="file-list">
            {existingFiles.map(file => (
              <div className="file-item" key={file._id}>
                <span className="file-item-name">
                  <img src={fileIcon} alt="업로드 파일" />
                  {file.originalName}
                </span>
                <span
                  className="file-item-remove"
                  onClick={() => handleExistingFileRemove(file._id)}
                >
                  <img src={closeIcon} alt="파일 제거" />
                </span>
              </div>
            ))}

            {newFiles.map((file, index) => (
               <div className="file-item" key={file.name + index}>
                  <span className="file-item-name">
                    <img src={fileIcon} alt="업로드 파일" />
                    {file.name}
                  </span>
                  <span className="file-item-remove" onClick={() => handleNewFileRemove(index)}>
                    <img src={closeIcon} alt="파일 제거" />
                  </span>
                </div>
            ))}
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="back-btn"
            onClick={() => {
              navigate(`/setting/faq-list`);
              onEditSuccess?.();
            }}
          >
            취소
          </button>
          <button type="submit" className="submit-btn" disabled={isSubmitting}>
            {isSubmitting ? <LoadingSpinner /> : mode === 'edit' ? '수정하기' : '등록하기'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default FAQ;

const LoadingSpinner = () => <div className="loading-spinner__spinner"></div>;
